/**
 * Portfolio-wide compliance computation. Runs the deterministic MFN engine
 * across every fund in the tenant and rolls the results into the numbers the
 * dashboard leads with — the single compliance-posture score, the live
 * conflict radar (breaches + elevated flags), and category/exposure breakdowns.
 */
import { listFunds, type Fund } from "@/lib/api/funds";
import { gatherFundMfnContext, evaluateLive, type EnrichedTrigger } from "@/lib/mfn/engine-context";

export interface RadarTrigger extends EnrichedTrigger {
  fundId: string;
  fundName: string;
}

export interface PortfolioRadar {
  funds: Fund[];
  triggers: RadarTrigger[];
  breaches: RadarTrigger[];
  elevated: RadarTrigger[];
  review: RadarTrigger[];
  /** Sum of holder commitments under an active (breach-severity) entitlement. */
  exposedCommitment: number;
  /** 0–100 compliance posture: starts at 100, weighted deductions per finding. */
  postureScore: number;
}

export async function computePortfolioRadar(tenantId: string): Promise<PortfolioRadar> {
  const funds = await listFunds(tenantId);

  const perFund = await Promise.all(
    funds.map(async (f) => {
      const ctx = await gatherFundMfnContext(tenantId, f.id);
      return evaluateLive(ctx).map<RadarTrigger>((t) => ({
        ...t,
        fundId: f.id,
        fundName: f.short_name ?? f.name,
      }));
    }),
  );

  const triggers = perFund.flat();
  const breaches = triggers.filter((t) => t.severity === "breach");
  const elevated = triggers.filter((t) => t.severity === "elevated");
  const review = triggers.filter((t) => t.severity === "review");

  const exposedCommitment = breaches.reduce((sum, t) => sum + (t.holderCommitment ?? 0), 0);

  // Posture: each breach -12, each elevated -5, each review -1.5, floored at 0.
  const deduction = breaches.length * 12 + elevated.length * 5 + review.length * 1.5;
  const postureScore = Math.max(0, Math.round(100 - deduction));

  return { funds, triggers, breaches, elevated, review, exposedCommitment, postureScore };
}
